import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export type FeatureFlag = {
  id: string;
  name: string;
  enabled: boolean;
};

export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const controller = new AbortController();

    (async () => {
      try {
        const { data, error: dbError } = await supabase
          .from("feature_flags" as any)
          .select("id, name, enabled")
          .order("name")
          .abortSignal(controller.signal);

        if (!mountedRef.current) return;

        if (dbError) {
          console.error("[useFeatureFlags] fetch error:", dbError);
          setError(new Error(dbError.message));
        } else if (data) {
          setFlags(data as FeatureFlag[]);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        // AbortError is expected on unmount — don't log or set error state
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[useFeatureFlags] unexpected error:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, []);

  const isEnabled = (name: string) => flags.find((f) => f.name === name)?.enabled ?? false;

  const toggle = async (id: string, enabled: boolean) => {
    await supabase
      .from("feature_flags" as any)
      .update({ enabled, updated_at: new Date().toISOString() })
      .eq("id", id);
    setFlags((prev) => prev.map((f) => (f.id === id ? { ...f, enabled } : f)));
  };

  return { flags, loading, error, isEnabled, toggle };
}
